import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { WalletOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { currency } from './portfolioHoldingPanels';
import './PortfolioStickyOverview.css';

function avgNumeric(rows, key) {
  const nums = rows
    .map((r) => Number(r[key]))
    .filter((n) => Number.isFinite(n));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

export default function PortfolioStickyOverview({
  strategy,
  categoryInfo,
  holdings = [],
  loading = false,
  isAuthenticated = false,
  onScrollToHoldings,
}) {
  const navigate = useNavigate();
  const stats = useMemo(() => {
    const totalMv = holdings.reduce((sum, r) => {
      const n = Number(r.totalMarketValue);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    return {
      count: holdings.length,
      totalMv,
      avgYield: avgNumeric(holdings, 'yield'),
      avgCoupon: avgNumeric(holdings, 'coupon'),
    };
  }, [holdings]);

  const title = categoryInfo?.name || strategy?.title || 'Portfolio';
  const subtitle = categoryInfo
    ? `Bonds added while exploring ${categoryInfo.name}`
    : `Bonds saved under ${strategy?.title || 'this strategy'}`;

  return (
    <aside className="pso-card" aria-label="Portfolio overview">
      <div className="pso-card__header">
        <div className="pso-card__header-icon" aria-hidden>
          <WalletOutlined />
        </div>
        <div className="pso-card__header-text">
          <h3 className="pso-card__title">Your basket</h3>
          <p className="pso-card__category" title={title}>{title}</p>
        </div>
      </div>

      <p className="pso-card__subtitle">{subtitle}</p>

      {!isAuthenticated ? (
        <p className="pso-card__empty">Log in to track bonds you add from this category.</p>
      ) : loading ? (
        <div className="pso-card__loading"><Spin size="small" /></div>
      ) : stats.count === 0 ? (
        <p className="pso-card__empty">
          No bonds added yet. Use <strong>Add</strong> on any bond below to build your basket.
        </p>
      ) : (
        <>
          <div className="pso-card__metrics">
            <div className="pso-card__metric pso-card__metric--value">
              <span className="pso-card__metric-label">Total value</span>
              <span className="pso-card__metric-value" title={currency(stats.totalMv)}>
                {currency(stats.totalMv)}
              </span>
            </div>
            <div className="pso-card__metrics-row">
              <div className="pso-card__metric">
                <span className="pso-card__metric-value">{stats.count}</span>
                <span className="pso-card__metric-label">{stats.count === 1 ? 'Bond' : 'Bonds'}</span>
              </div>
              <div className="pso-card__metric">
                <span className="pso-card__metric-value">
                  {stats.avgYield != null ? `${stats.avgYield.toFixed(2)}%` : '—'}
                </span>
                <span className="pso-card__metric-label">Avg yield</span>
              </div>
            </div>
          </div>

          <ul className="pso-card__list">
            {holdings.slice(0, 6).map((row) => (
              <li key={row.isin} className="pso-card__list-item">
                <div className="pso-card__list-main">
                  <span className="pso-card__issuer" title={row.issuer}>
                    {row.issuer || row.isin}
                  </span>
                  <code className="pso-card__isin">{row.isin}</code>
                </div>
                <span className="pso-card__mv" title={currency(row.totalMarketValue)}>
                  {currency(row.totalMarketValue)}
                </span>
              </li>
            ))}
            {holdings.length > 6 && (
              <li className="pso-card__list-more">+{holdings.length - 6} more in basket</li>
            )}
          </ul>
        </>
      )}

      {isAuthenticated && stats.count > 0 && onScrollToHoldings && (
        <button type="button" className="pso-card__link" onClick={onScrollToHoldings}>
          View full holdings ↓
        </button>
      )}

      {isAuthenticated && stats.count > 0 && (
        <button type="button" className="pso-card__link pso-card__link--secondary" onClick={() => navigate('/my-portfolio')}>
          Portfolio analytics →
        </button>
      )}
    </aside>
  );
}
